/**
 * A record view as the record page lays it out (04_frontend_spec.md §6.6):
 * header, available commands, children with row commands, related lists,
 * and the layout the declaration asks for: fact groups, a Total row under a
 * child table, a detail on hover (docs/object-contract.md, "Laying out a
 * record"). Pure: the page renders what this returns.
 */
import { paths } from "../../routing/routes";
import { parseFormattedText, type FormattedBlock } from "../formatted-text";
import type {
  ChildRow,
  ChildRows,
  Command,
  FileValue,
  ManifestChild,
  ManifestChildGrid,
  ManifestChildStack,
  ManifestColumn,
  ManifestList,
  ManifestObject,
  Paginated,
  RecordNotice,
  RecordView,
  StateTone,
} from "./contract";
import { idColumnFields, isIdentifierReadable } from "./list-columns";
import { rowTarget } from "./list-includes";
import {
  emptyListQuery,
  formatListQuery,
  parseListQuery,
  type ListQuery,
} from "./list-query";
import type { ManifestIndex } from "./manifest";
import { refsOf, type PresentedRef } from "./refs";
import {
  filesOf,
  formatColumnValue,
  formatValue,
  linkHref,
  stateToneOf,
} from "./values";

/** A second field shown on hover and keyboard focus over a fact or cell (`cis_field(detail=...)`): "VAT rate 20%". */
export interface PresentedDetail {
  label: string;
  value: string;
}

/** The detail a column declares, read from the record or row; null where it declares none or the field is empty. */
export function detailOf(
  column: ManifestColumn,
  source: Record<string, unknown>,
): PresentedDetail | null {
  const detail = column.detail;
  if (!detail) return null;
  const raw = source[detail.field];
  if (raw === null || raw === undefined || raw === "") return null;
  return { label: detail.label, value: formatColumnValue(raw, detail) };
}

/**
 * The column a cell draws: the one it declares as `fallback` while its own value is null or empty and the row holds a
 * value for the fallback (`cis_field(fallback=...)`), else the column itself. Read the cell's value, links, files and
 * kind off what this returns; the header keeps the declared column's label. Twin of `_shown` in
 * `apps/api/app/operational_mcp/present_record.py`.
 */
export function shownColumnOf(
  column: ManifestColumn,
  source: Record<string, unknown>,
): ManifestColumn {
  const fallback = column.fallback;
  if (
    !fallback ||
    !isBlank(source[column.field]) ||
    isBlank(source[fallback.field])
  )
    return column;
  return fallback;
}

const isBlank = (value: unknown) =>
  value === null ||
  value === undefined ||
  value === "" ||
  (Array.isArray(value) && value.length === 0);

/** The files a `file` column's value holds that can be opened; null for any other column, or none to open. */
export function cellFilesOf(
  column: Pick<ManifestColumn, "field" | "kind">,
  source: Record<string, unknown>,
): FileValue[] | null {
  if (column.kind !== "file") return null;
  const files = filesOf(source[column.field]);
  return files.length > 0 ? files : null;
}

/** The blocks a `formatted_text` column's value holds; null for any other column or an empty value. */
export function cellFormattedOf(
  column: Pick<ManifestColumn, "field" | "kind">,
  source: Record<string, unknown>,
): FormattedBlock[] | null {
  if (column.kind !== "formatted_text") return null;
  const value = source[column.field];
  if (typeof value !== "string") return null;
  const blocks = parseFormattedText(value);
  return blocks.length > 0 ? blocks : null;
}

export interface PresentedFact {
  /** The record field the fact shows (what a setting command names in `edits`). */
  field: string;
  label: string;
  value: string;
  /** The record's own value, before formatting (an on/off setting reads it). */
  raw: unknown;
  /** A fact that names other records (`ref`): each one, with its page where it has one. */
  refs?: PresentedRef[];
  /** The refs are one chain (`path`), drawn joined by ›. */
  isPath?: true;
  /** A `file` fact's files, each opened with the person's sign-in (`filesOf`); absent where it holds none to open. */
  files?: FileValue[];
  /** A `formatted_text` fact's blocks, drawn as elements by the shell (`value` is the text as written). */
  formatted?: FormattedBlock[];
  /** A `link` fact's URL to open in a new tab, only when it is an absolute
   *  http(s) URL (`linkHref`); absent otherwise, and the value shows as text. */
  href?: string;
  /** The words the link shows in place of its URL, where the fact declares them (`link_text`). */
  linkText?: string;
  /** The record holds nothing for it (null, blank or an empty list): the header's fact line leaves it out. */
  isEmpty: boolean;
  /** Free text too long for a line of facts, or with line breaks of its own: drawn as a block beneath them. */
  isLongText: boolean;
  /** The second field shown on hover over the value, where the fact declares one and it holds something. */
  detail?: PresentedDetail;
}

/** Facts drawn together on a line of their own (`record.fact_groups`), by record field. */
export interface PresentedFactGroup {
  label: string | null;
  fields: string[];
  /** The section the group draws in, under that heading; null for a group drawn with the header's facts. */
  section: string | null;
  /** False where the section draws without its heading. */
  sectionHeading: boolean;
  /** Drawn as one bold line with no labels. */
  isTitle: boolean;
}

/** A record's section: its heading and its groups, each a line of short facts or a long text drawn in place. */
export interface PresentedFactSection {
  label: string;
  /** False where the section draws without its heading. */
  hasHeading: boolean;
  groups: { label: string | null; facts: PresentedFact[]; isTitle: boolean }[];
}

/** A text fact longer than this is read as a passage, not as a short value in a line of facts (provisional). */
export const LONG_TEXT_FACT_LENGTH = 80;

function isEmptyFact(raw: unknown): boolean {
  return (
    raw === null ||
    raw === undefined ||
    (typeof raw === "string" && raw.trim() === "") ||
    (Array.isArray(raw) && raw.length === 0)
  );
}

/**
 * Where the header draws the facts: those with a value in its fact line, a grouped fact on its group's own line
 * under it, a long text in a block under those, one with nothing left out (a group left with none is not drawn). A
 * fact a command edits (`isSetting`) is drawn whatever it holds, so the person can set it. Claude's `get_record`
 * keeps every fact (the empty one as "–") and names the groups; this is only how the page lays them out.
 */
export function headerFactsOf(
  facts: readonly PresentedFact[],
  isSetting: (field: string) => boolean,
  groups: readonly PresentedFactGroup[] = [],
): {
  line: PresentedFact[];
  groups: { label: string | null; facts: PresentedFact[] }[];
  blocks: PresentedFact[];
  sections: PresentedFactSection[];
} {
  const shown = (fact: PresentedFact) => isSetting(fact.field) || !fact.isEmpty;
  const grouped = new Set(groups.flatMap((g) => g.fields));
  const byField = new Map(facts.map((f) => [f.field, f]));
  const line: PresentedFact[] = [];
  const blocks: PresentedFact[] = [];
  for (const fact of facts) {
    if (grouped.has(fact.field) || !shown(fact)) continue;
    if (fact.isLongText && !isSetting(fact.field)) blocks.push(fact);
    else line.push(fact);
  }
  const membersOf = (group: PresentedFactGroup) =>
    group.fields.flatMap((field) => {
      const fact = byField.get(field);
      return fact && shown(fact) ? [fact] : [];
    });
  const drawnGroups = groups.flatMap((group) => {
    if (group.section) return [];
    const members = membersOf(group);
    return members.length > 0 ? [{ label: group.label, facts: members }] : [];
  });
  const sections: PresentedFactSection[] = [];
  for (const group of groups) {
    const members = membersOf(group);
    if (!group.section || members.length === 0) continue;
    let section = sections.find((s) => s.label === group.section);
    if (!section) {
      section = { label: group.section, hasHeading: true, groups: [] };
      sections.push(section);
    }
    if (!group.sectionHeading) section.hasHeading = false;
    section.groups.push({
      label: group.label,
      facts: members,
      isTitle: group.isTitle,
    });
  }
  return { line, groups: drawnGroups, blocks, sections };
}

export interface PresentedChildRow {
  key: string;
  /**
   * What heads the row's commands (their dialog's context): its `title_field` value where the child declares one and the
   * row has it, else its key.
   */
  commandHeading: string;
  cells: string[];
  /** Per cell, the records a `ref` column names; null for a cell that names none. */
  cellRefs: (PresentedRef[] | null)[];
  /** Per cell, the detail shown on hover (`cis_field(detail=...)`); null for a cell with none. */
  cellDetails: (PresentedDetail | null)[];
  /** Per cell, the files a `file` column holds that can be opened; null for a cell with none. */
  cellFiles: (FileValue[] | null)[];
  /** Per cell, the blocks a `formatted_text` column holds; null for a cell with none. */
  cellFormatted: (FormattedBlock[] | null)[];
  /** Per cell, the row's own value before formatting (a setting's switch reads it, `rowSettingsOf`). */
  cellRaws: unknown[];
  commands: Command[];
  /** The row's own record page, where its object has one (an invoice's credit notes); else null. */
  href: string | null;
}

/**
 * A cell that holds nothing, as the page draws it: blank, or "–" (null, an empty string or an empty list), with no
 * record or file to open. A phone's stacked row leaves it out (Ben, 5 Nov), where a desktop table keeps the column. 0
 * and false are values ("0", "No"), never empty.
 */
export function isEmptyCell(row: PresentedChildRow, n: number): boolean {
  const cell = (row.cells[n] ?? "").trim();
  return (
    (cell === "" || cell === "–") &&
    !(row.cellRefs[n]?.length ?? 0) &&
    !(row.cellFiles[n]?.length ?? 0)
  );
}

/** A Total row's figure that holds nothing: none for its column, blank or "–". A phone leaves it out. */
export function isEmptyFigure(figure: string | null): boolean {
  return figure === null || figure.trim() === "" || figure.trim() === "–";
}

/** One cell of a grid: a child row's value, its state text, whether it is marked, and its own commands. */
export interface PresentedGridCell {
  /** The child row's key, which a command on the cell runs against. */
  key: string;
  /** What heads the cell's commands (`PresentedChildRow.commandHeading`). */
  commandHeading: string;
  /** The cell value as formatted; empty where the row has none (its state is then what shows). */
  text: string;
  /** The owner's text for the cell ("Overdue", "Chosen"); null where it has none. */
  state: string | null;
  isHighlighted: boolean;
  /** The cell's own record (a price in a dictionary item's price grid), where the child has record pages. */
  href: string | null;
  commands: Command[];
}

export interface PresentedGridRow {
  key: string;
  label: string;
  /** One per column of the grid, null where the row has no value for it. */
  cells: (PresentedGridCell | null)[];
}

/** One small grid: the columns the group's rows have, in order, and a row for each of its labelled rows. */
export interface PresentedGridGroup {
  key: string;
  /** What heads the grid (the group's value); null where the grid is not grouped. */
  heading: string | null;
  columns: { key: string; label: string }[];
  rows: PresentedGridRow[];
}

const present = (value: unknown) =>
  value !== null && value !== undefined && value !== "";

/**
 * A child's rows as grids (`children[].grid`): the rows grouped by `group_field`, each group's columns the distinct
 * `column_field` values (a number sorts as one, else in the order met), its grid rows the distinct `row_field` values
 * in the order met, labelled by their first row's `row_label_field`; a row with no label is left out. The shell
 * compares nothing: the state text and the mark come from the rows.
 */
export function presentChildGrid(
  child: Pick<ManifestChild, "columns">,
  grid: ManifestChildGrid,
  rows: readonly ChildRow[],
  keyOf: (row: ChildRow, i: number) => string,
  commandHeadingOf: (row: ChildRow, i: number) => string = keyOf,
  hrefOf: (row: ChildRow) => string | null = () => null,
): PresentedGridGroup[] {
  const columnOf = (field: string) =>
    child.columns.find((c) => c.field === field);
  const cellColumn = columnOf(grid.cell_field);
  const headingColumn = columnOf(grid.column_field);
  if (!cellColumn || !headingColumn) {
    throw new Error(
      `The grid's cell (${grid.cell_field}) and column (${grid.column_field}) fields must be columns of the rows.`,
    );
  }
  const groups = new Map<string, { row: ChildRow; i: number }[]>();
  rows.forEach((row, i) => {
    if (!present(row[grid.row_label_field])) return;
    const group = grid.group_field ? String(row[grid.group_field] ?? "") : "";
    groups.set(group, [...(groups.get(group) ?? []), { row, i }]);
  });
  return [...groups].map(([group, members]) => {
    const raws = [
      ...new Set(members.map(({ row }) => row[grid.column_field])),
    ].filter(present);
    if (raws.every((v) => typeof v === "number"))
      (raws as number[]).sort((a, b) => a - b);
    const rowKeys = [
      ...new Set(members.map(({ row }) => String(row[grid.row_field]))),
    ];
    return {
      key: group,
      heading: grid.group_field ? group : null,
      columns: raws.map((raw) => ({
        key: String(raw),
        label: formatColumnValue(raw, headingColumn),
      })),
      rows: rowKeys.map((rowKey) => {
        const own = members.filter(
          ({ row }) => String(row[grid.row_field]) === rowKey,
        );
        return {
          key: rowKey,
          label: String(own[0]!.row[grid.row_label_field]),
          cells: raws.map((raw) => {
            const found = own.find(({ row }) => row[grid.column_field] === raw);
            if (!found) return null;
            const state = grid.state_field
              ? found.row[grid.state_field]
              : undefined;
            return {
              key: keyOf(found.row, found.i),
              commandHeading: commandHeadingOf(found.row, found.i),
              text: present(found.row[grid.cell_field])
                ? formatColumnValue(found.row[grid.cell_field], cellColumn)
                : "",
              state: present(state) ? String(state) : null,
              isHighlighted: grid.highlight_field
                ? found.row[grid.highlight_field] === true
                : false,
              href: hrefOf(found.row),
              commands: found.row.commands ?? [],
            };
          }),
        };
      }),
    };
  });
}

/** One stacked block: a child row read as prose, its heading and its lines. */
export interface PresentedStackBlock {
  key: string;
  /** What heads the block's commands (`PresentedChildRow.commandHeading`). */
  commandHeading: string;
  /** The heading's parts in order, each linked where its column names a record. */
  heading: { text: string; refs: PresentedRef[] | null }[];
  /** The row's state, as its label; null where the child has no state column. */
  state: string | null;
  /** The tone of the state's badge, as the child object declares it. */
  stateTone: StateTone;
  /** The row's own page, where the child object has one. */
  href: string | null;
  /** Show the heading only (a decided action), with `foldedDetail` beside the state. */
  isFolded: boolean;
  foldedDetail: string | null;
  lines: {
    label: string | null;
    text: string | null;
    items: { text: string; note: string | null }[];
  }[];
  commands: Command[];
}

/**
 * A child's rows as stacked blocks (`children[].stack`). The heading's parts are columns of the rows, formatted and
 * linked as a table's cells are; the state is the child's state column as its label; each line is the row's own text
 * and points, an empty one left out. A row in one of `folded_states` keeps its heading and only the lines marked
 * `when_folded`.
 */
export function presentChildStack(
  index: ManifestIndex,
  child: Pick<ManifestChild, "columns"> &
    Partial<Pick<ManifestChild, "object">>,
  stack: ManifestChildStack,
  rows: readonly ChildRow[],
  keyOf: (row: ChildRow, i: number) => string,
  hrefOf: (row: ChildRow) => string | null,
  commandHeadingOf: (row: ChildRow, i: number) => string = keyOf,
): PresentedStackBlock[] {
  const headingColumns = stack.heading_fields.map((field) => {
    const column = child.columns.find((c) => c.field === field);
    if (!column)
      throw new Error(
        `The stack's heading field ${field} must be a column of the rows.`,
      );
    return column;
  });
  const stateColumn = child.columns.find((c) => c.kind === "state") ?? null;
  const rowObject =
    child.object && index.isObjectKnown(child.object)
      ? index.objectOf(child.object)
      : null;
  const textOf = (value: unknown) =>
    present(value) && String(value).trim() !== "" ? String(value) : null;
  const itemsOf = (value: unknown) =>
    (Array.isArray(value) ? value : []).flatMap(
      (item: unknown): { text: string; note: string | null }[] => {
        if (typeof item === "string")
          return item.trim() ? [{ text: item, note: null }] : [];
        if (item && typeof item === "object" && "text" in item) {
          const { text, note } = item as { text: unknown; note?: unknown };
          return textOf(text)
            ? [{ text: String(text), note: textOf(note) }]
            : [];
        }
        return [];
      },
    );
  return rows.map((row, i) => {
    const isFolded =
      stateColumn !== null &&
      (stack.folded_states ?? []).includes(String(row[stateColumn.field]));
    return {
      key: keyOf(row, i),
      commandHeading: commandHeadingOf(row, i),
      heading: headingColumns.map((column) => ({
        text: formatColumnValue(row[column.field], column),
        refs: refsOf(index, column, row),
      })),
      state:
        stateColumn && present(row[stateColumn.field])
          ? formatColumnValue(row[stateColumn.field], stateColumn)
          : null,
      stateTone: stateColumn
        ? stateToneOf(rowObject, row[stateColumn.field])
        : "neutral",
      href: hrefOf(row),
      isFolded,
      foldedDetail:
        isFolded && stack.folded_detail_field
          ? textOf(row[stack.folded_detail_field])
          : null,
      // A folded block keeps only the lines marked `when_folded`, which the page shows behind a "Why" toggle.
      lines: stack.lines.flatMap(
        ({ label, field, items_field, when_folded }) => {
          if (isFolded && !when_folded) return [];
          const text = field ? textOf(row[field]) : null;
          const items = itemsOf(items_field ? row[items_field] : undefined);
          return text || items.length > 0 ? [{ label, text, items }] : [];
        },
      ),
      commands: row.commands ?? [],
    };
  });
}

export interface PresentedChild {
  object: string;
  label: string;
  /** The child's one-line note under its heading, when it has one. */
  note: string | null;
  columns: ManifestColumn[];
  rows: PresentedChildRow[];
  /** How many rows the record has in all: more than `rows` when the record sent its first rows only. */
  total: number;
  /** The child's own list filtered to this record, when the record sent only some of its rows. */
  viewAll: { href: string; label: string } | null;
  /**
   * The Total row under the rows (`children[].totals`): per column, the record's own figure for it (the owning
   * module's sum over every row) or null under a column it does not total. Null where the child totals nothing.
   */
  totals: { label: string; cells: (string | null)[] } | null;
  /** The rows as grids, where the child declares `grid`; the page then draws these in place of the table. */
  grid: PresentedGridGroup[] | null;
  /** The rows as stacked blocks, where the child declares `stack`; the page then draws these in place of the table. */
  stack: PresentedStackBlock[] | null;
}

/** "Total", or "Total of all 23" under a table showing its first rows only: the figure is every row's (provisional). */
export function totalRowLabel(shown: number, total: number): string {
  return shown < total ? `Total of all ${total}` : "Total";
}

export interface PresentedRelated {
  object: string;
  label: string;
  /** The related object's list, filtered by `params`, marked so its commands start with them (`related-prefill.ts`). */
  href: string;
  /** The filters this record sets on the related list, templates filled (`{lead_id}` → "L-1"). */
  params: Record<string, string>;
  /** Drawn as a link with its count on the "See also" line rather than as a section (`ManifestRelated.secondary`). */
  isSecondary: boolean;
}

/**
 * The query that reads a related list's first rows ("Emails (7)" and its first three, Ben's R39: a record says how many
 * and shows a few, never a sum or a value): exactly what the list's link opens, read back the way the list page reads
 * it (a declared default queue, a param the list does not declare), on page 1 of `pageSize` rows. The answer's `total`
 * is the count.
 */
export function relatedListQuery(
  list: ManifestList,
  related: Pick<PresentedRelated, "href">,
  pageSize: number,
): ListQuery {
  const at = related.href.indexOf("?");
  const search = at === -1 ? "" : related.href.slice(at + 1);
  return { ...parseListQuery(search, list), page: 1, page_size: pageSize };
}

/** How many of a related list's rows the record shows before "View all". */
export const RELATED_ROWS_SHOWN = 3;
/** How many of the related list's columns the record shows: the first few it declares that are not internal ids. */
const RELATED_COLUMNS_SHOWN = 4;

/**
 * A related list's first rows drawn as a child table of the record: the list's own first columns (an internal id is
 * not one), each row opening its record where its object has one, "View all {total} {plural} →" to the filtered list
 * when there are more. Rows carry no commands: the list is where a row is worked on.
 */
export function presentRelatedRows(
  index: ManifestIndex,
  related: PresentedRelated,
  page: Paginated<ChildRow>,
): PresentedChild {
  const object = index.objectOf(related.object);
  const list = object.list!;
  const hidden = idColumnFields(list, object.identifier.kind, page.items);
  const columns = list.columns
    .filter((c) => !hidden.has(c.field))
    .slice(0, RELATED_COLUMNS_SHOWN);
  const rows = page.items.slice(0, RELATED_ROWS_SHOWN).map((row, i) => {
    const target = rowTarget(index, related.object, list, row);
    const opens = index.objectOf(target.object).record
      ? index.hrefOf({ object: target.object, id: target.key })
      : null;
    return presentChildRow(
      index,
      columns,
      row,
      target.key === "" ? String(i) : target.key,
      target.key === "" ? null : opens,
    );
  });
  const total = Math.max(page.total, rows.length);
  return {
    object: related.object,
    label: related.label,
    note: null,
    columns,
    rows,
    total,
    viewAll:
      total > rows.length
        ? {
            href: related.href,
            label: `View all ${total} ${related.label.toLowerCase()} →`,
          }
        : null,
    totals: null,
    grid: null,
    stack: null,
  };
}

/** A state badge in the record's header: the state's label, and the tone its object gives it. */
export interface PresentedState {
  label: string;
  tone: StateTone;
}

export interface PresentedRecord {
  identifier: string;
  title: string;
  /** What names the record in a breadcrumb or a label: its number, or its title where the identifier is an internal id. */
  name: string;
  /** "Order ORD-K48193 · Spring catalogue reprint"; an object whose identifier is an internal id is headed by its title ("Stock item SRA3-170-SILK"). */
  heading: string;
  states: PresentedState[];
  /** What the record's current state means and what will happen, as the object declares it; empty where it declares none. */
  stateMeanings: string[];
  /** Facts that change what the main command will do, without blocking it: drawn above the header's buttons. */
  notices: RecordNotice[];
  facts: PresentedFact[];
  /** The facts the object draws together on a line of their own; every one is also in `facts`. */
  factGroups: PresentedFactGroup[];
  commands: Command[];
  children: PresentedChild[];
  related: PresentedRelated[];
}

/** Fill `{field}` templates from the record; null when a field is absent. */
function fillTemplate(template: string, record: RecordView): string | null {
  let missing = false;
  const filled = template.replace(/\{([^}]+)\}/g, (_, field: string) => {
    const value = record[field];
    if (value === null || value === undefined || value === "") {
      missing = true;
      return "";
    }
    return String(value);
  });
  return missing ? null : filled;
}

/**
 * A child row's record page: only for a child object with a record of its own (or the object its rows `opens`),
 * keyed by that object's identifier as the row carries it. A row without the identifier gets no link rather than a
 * guessed one.
 */
function childRecordHref(
  index: ManifestIndex,
  childObject: string,
): (row: ChildRow) => string | null {
  // Known, not enabled: a row's link still resolves (to the object's "switched off" page).
  if (!index.isObjectKnown(childObject)) return () => null;
  const declared = index.objectOf(childObject);
  if (!declared.record) return () => null;
  return (row) => {
    const id = row[declared.identifier.field];
    return id === null || id === undefined || id === ""
      ? null
      : index.hrefOf({ object: childObject, id: String(id) });
  };
}

/** A child table's rows on the record and how many it has: a paged child's `ChildRows`, or every row. */
function childRowsOf(
  child: ManifestChild,
  record: RecordView,
): { rows: ChildRow[]; total: number } {
  const value = record[child.field];
  if (child.paged) {
    const paged = value as ChildRows | null | undefined;
    const rows = Array.isArray(paged?.rows) ? paged.rows : [];
    return { rows, total: Math.max(paged?.total ?? 0, rows.length) };
  }
  const rows = Array.isArray(value) ? (value as ChildRow[]) : [];
  return { rows, total: rows.length };
}

/**
 * "View all 809 addresses →": the child object's list filtered to this
 * record by the manifest's `parent_filter` (its value is the record's field
 * of that name, else the record's key), marked so the list's commands start
 * with it. None where the list is switched off for the person or the filter
 * has no value.
 */
function viewAllOf(
  index: ManifestIndex,
  object: ManifestObject,
  child: ManifestChild,
  record: RecordView,
  total: number,
): { href: string; label: string } | null {
  const param = child.parent_filter;
  if (!param || !index.isObjectEnabled(child.object)) return null;
  if (!index.objectOf(child.object).list) return null;
  const own = record[param];
  const value =
    typeof own === "string" && own !== ""
      ? own
      : param === object.identifier.field
        ? String(record[object.identifier.field] ?? "")
        : "";
  if (value === "") return null;
  const query = emptyListQuery();
  query.filters[param] = value;
  query.prefill.push(param);
  return {
    href: paths.objectList({
      objectId: child.object,
      search: formatListQuery(query),
    }),
    label: `View all ${total} ${child.label.toLowerCase()} →`,
  };
}

/** The Total row of a child that declares `totals`: the record's figures, formatted by their own fields' kinds. */
function totalsOf(
  child: ManifestChild,
  record: RecordView,
  shown: number,
  total: number,
): PresentedChild["totals"] {
  const totals = child.totals ?? {};
  if (Object.keys(totals).length === 0) return null;
  return {
    label: totalRowLabel(shown, total),
    cells: child.columns.map((column) => {
      const figure = totals[column.field];
      return figure ? formatColumnValue(record[figure.field], figure) : null;
    }),
  };
}

/**
 * What heads a child row's commands: the row's `title_field` value (".01 → Acme Ltd, Exeter") where the child declares
 * one and the row carries it, else the row's key, as before the child named one.
 */
export function childCommandHeadingOf(
  child: Pick<ManifestChild, "title_field">,
  keyOf: (row: ChildRow, i: number) => string,
): (row: ChildRow, i: number) => string {
  const field = child.title_field;
  return (row, i) => {
    const title = field ? row[field] : undefined;
    return typeof title === "string" && title.trim() !== ""
      ? title
      : keyOf(row, i);
  };
}

/** One table row as drawn: each column's cell formatted by its kind, with the links, files and details it carries. */
function presentChildRow(
  index: ManifestIndex,
  columns: readonly ManifestColumn[],
  row: ChildRow,
  key: string,
  href: string | null,
  commandHeading: string = key,
): PresentedChildRow {
  const shown = columns.map((c) => shownColumnOf(c, row));
  return {
    key,
    commandHeading,
    cells: shown.map((c) => formatColumnValue(row[c.field], c)),
    cellRefs: shown.map((c) => refsOf(index, c, row)),
    cellDetails: columns.map((c) => detailOf(c, row)),
    cellFiles: shown.map((c) => cellFilesOf(c, row)),
    cellFormatted: shown.map((c) => cellFormattedOf(c, row)),
    cellRaws: shown.map((c) => row[c.field]),
    commands: row.commands ?? [],
    href,
  };
}

export function presentRecord(
  index: ManifestIndex,
  object: ManifestObject,
  record: RecordView,
): PresentedRecord {
  const declared = object.record;
  if (!declared) {
    throw new Error(`${object.id} is list-only: it has no record page.`);
  }
  const identifier = String(record[object.identifier.field] ?? "");
  const title = String(record[declared.title_field] ?? "");
  const states = declared.state_fields.flatMap((field) => {
    const value = record[field];
    return value === null || value === undefined || value === ""
      ? []
      : [
          {
            label: formatValue(value, "state", declared.state_labels?.[field]),
            tone: stateToneOf(object, value),
          },
        ];
  });
  const stateMeanings = [
    ...new Set(
      declared.state_fields.flatMap((field) => {
        const meaning = object.state_meanings?.[String(record[field] ?? "")];
        return meaning ? [meaning] : [];
      }),
    ),
  ];
  const facts = declared.facts.map((fact): PresentedFact => {
    const href = fact.kind === "link" ? linkHref(record[fact.field]) : null;
    const refs = refsOf(index, fact, record);
    const value = formatColumnValue(record[fact.field], fact);
    const detail = detailOf(fact, record);
    const files = cellFilesOf(fact, record);
    const formatted = cellFormattedOf(fact, record);
    return {
      field: fact.field,
      label: fact.label,
      value,
      raw: record[fact.field],
      ...(href ? { href } : {}),
      ...(href && fact.link_text ? { linkText: fact.link_text } : {}),
      ...(refs ? { refs } : {}),
      ...(refs && fact.path ? { isPath: true as const } : {}),
      ...(files ? { files } : {}),
      ...(formatted ? { formatted } : {}),
      ...(detail ? { detail } : {}),
      isEmpty: isEmptyFact(record[fact.field]),
      isLongText:
        fact.kind === "formatted_text" ||
        (fact.kind === "text" &&
          !refs &&
          !href &&
          (value.length > LONG_TEXT_FACT_LENGTH || value.includes("\n"))),
    };
  });
  // A child sent as null does not apply to this record (a purchase-order invoice has no usage records): it is not
  // drawn at all, where an empty list is drawn as "None yet".
  const applicable = object.children.filter(
    (child) => record[child.field] !== null,
  );
  const children = applicable.map((child): PresentedChild => {
    const { rows, total } = childRowsOf(child, record);
    const hrefOf = childRecordHref(index, child.opens ?? child.object);
    const rowKey = (row: ChildRow, i: number) =>
      String(row[child.key_field] ?? i);
    const commandHeadingOf = childCommandHeadingOf(child, rowKey);
    return {
      object: child.object,
      label: child.label,
      note: child.note ?? null,
      columns: child.columns,
      rows: rows.map((row, i) =>
        presentChildRow(
          index,
          child.columns,
          row,
          rowKey(row, i),
          hrefOf(row),
          commandHeadingOf(row, i),
        ),
      ),
      total,
      viewAll:
        total > rows.length
          ? viewAllOf(index, object, child, record, total)
          : null,
      totals: totalsOf(child, record, rows.length, total),
      grid: child.grid
        ? presentChildGrid(
            child,
            child.grid,
            rows,
            rowKey,
            commandHeadingOf,
            hrefOf,
          )
        : null,
      stack: child.stack
        ? presentChildStack(
            index,
            child,
            child.stack,
            rows,
            rowKey,
            hrefOf,
            commandHeadingOf,
          )
        : null,
    };
  });
  const related = object.related.flatMap((rel): PresentedRelated[] => {
    // A switched-off object has no records to list.
    if (!index.isObjectEnabled(rel.object) || !index.objectOf(rel.object).list)
      return [];
    const query = emptyListQuery();
    const params: Record<string, string> = {};
    for (const [param, template] of Object.entries(rel.params)) {
      const value = fillTemplate(template, record);
      if (value === null) {
        console.warn("object_ui.related.template_unfilled", {
          object: object.id,
          related: rel.object,
          param,
        });
        return [];
      }
      if (param === "q") query.q = value;
      else {
        query.filters[param] = value;
        query.prefill.push(param);
        params[param] = value;
      }
    }
    return [
      {
        object: rel.object,
        label: rel.label,
        href: paths.objectList({
          objectId: rel.object,
          search: formatListQuery(query),
        }),
        params,
        isSecondary: rel.secondary === true,
      },
    ];
  });
  // An internal id (a declared uuid, or `aud_…`) is the record's address, not something a person reads: the title names
  // it (a stock item by its code).
  const isIdentifierShown = isIdentifierReadable(object.identifier, identifier);
  return {
    identifier,
    title,
    name: isIdentifierShown ? identifier : title,
    heading: isIdentifierShown
      ? [`${object.label} ${identifier}`, title].filter(Boolean).join(" · ")
      : [object.label, title].filter(Boolean).join(" "),
    states,
    stateMeanings,
    notices: record.notices ?? [],
    facts,
    factGroups: (declared.fact_groups ?? []).map((g) => ({
      label: g.label ?? null,
      fields: g.fields,
      section: g.section ?? null,
      sectionHeading: g.section_heading ?? true,
      isTitle: g.style === "title",
    })),
    commands: record.commands ?? [],
    children,
    related,
  };
}
